import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';

import Swal from 'sweetalert2';
import {
  Pencil,
  Trash2,
  Plus
} from 'lucide-react';

function IndemnityLandingPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/indemnity-landing-pages');
      setPages(response.data.pages || []);
    } catch (err) {
      setError('Failed to fetch indemnity landing pages');
      console.error('Error fetching pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      title,
      slug: formData.slug || generateSlug(title)
    });
  };

  const handleCreatePage = async () => {
    if (!formData.title) {
      alert('Please enter a title');
      return;
    }
    if (!formData.slug) {
      alert('Please enter a slug');
      return;
    }

    try {
      const response = await api.post('/admin/indemnity-landing-pages', formData);
      navigate(`/admin/indemnity-landing-pages/${response.data.page.id}/edit`);
    } catch (err) {
      console.error('Create page error:', err);
      alert(err.response?.data?.error || 'Failed to create indemnity landing page');
    }
  };

  const handleDeletePage = async (pageId, pageTitle) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete "<strong>${pageTitle || 'this page'}</strong>"<br><br>This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/indemnity-landing-pages/${pageId}`);
        setSuccessMessage('Indemnity landing page has been deleted successfully.');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
        fetchPages();
      } catch (err) {
        console.error('Delete page error:', err);
        const error = err.response?.data?.error || 'Failed to delete page';
        setErrorMessage(error);
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="INDEMNITY_LANDING">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="INDEMNITY_LANDING">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">INDEMNITY LANDING PAGES</h1>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 text-white px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-teal-800 text-white">
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pages.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-sm text-gray-500">
                      No indemnity landing pages created yet. Create your first page to start.
                    </td>
                  </tr>
                ) : (
                  pages.map((page) => (
                    <tr 
                      key={page.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/indemnity-landing-pages/${page.id}/edit`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{page.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {page.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {page.is_active ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/indemnity-landing-pages/${page.id}/edit`);
                            }}
                            className="text-teal-600 hover:text-teal-800"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePage(page.id, page.title);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Footer with Add Button */}
          <div className="px-6 py-4 flex justify-between items-center">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
              title="Create New Page"
            >
              <Plus className="w-8 h-8" />
            </button>

            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {pages.length} page{pages.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Create Page Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Create New Indemnity Landing Page</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Indemnity Form"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="indemnity-form"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL-friendly identifier (e.g., indemnity-form)</p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ title: '', slug: '' });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePage}
                  className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors"
                >
                  Create & Edit Page
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default IndemnityLandingPages;

